import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { Bot, Check, Droplets, TriangleAlert } from 'lucide-react';

import { MathTextBlock } from '../../components/MathTextBlock';
import {
  factoryService,
  type FactoryBatch,
  type FactoryCriticalZoneItem,
  type FactoryTtqAdmissionResult,
  type FactoryTtqProduct,
  type FactoryTtqProductMember,
} from '../../services/factoryService';
import type { FactoryDeskOutletContext } from '../deskOutlet';
import { useFactoryOrderScope } from '../orderScope';
import { TaskProductsOpsPanel } from '../components/TaskProductsOpsPanel';
import { resolveAdmissionParticleQa } from '../utils/resolveAdmissionParticleQa';
import { truncateMathAwarePreview } from '../utils/truncateMathAwarePreview';
import {
  inductionTargetByMembers,
  readInductionBankMemberRatio,
} from '../inductionBankPolicy';

type BankDraft = {
  id?: string;
  title?: string;
  questions?: { main?: string };
  answers?: { main?: string };
  metadata?: {
    displayTitle?: string;
    embedImageKind?: 'GT' | 'NGT' | null;
    needsEmbeddedImage?: boolean;
    inducedFigure?: {
      status?: string;
      embedImageKind?: string | null;
      errorMessage?: string | null;
    };
    relatedGraphEmbeddedImage?: {
      sourceImageUrl?: string;
    };
  };
};

/** Induction bank target = min(L, max(⌈L/ratio⌉, 1)). */
function resolveIpttqBankTarget(
  memberCount: number,
  meta?: Record<string, unknown> | null,
): number {
  return inductionTargetByMembers(memberCount, readInductionBankMemberRatio(meta));
}

function productBankShort(p: { memberCount?: number; bankDrafts?: unknown; meta?: Record<string, unknown> }): boolean {
  const bankN = Array.isArray(p.bankDrafts) ? p.bankDrafts.length : 0;
  return bankN < resolveIpttqBankTarget(Number(p.memberCount) || 0, p.meta);
}

function batchMetaBankShortfall(batch?: FactoryBatch | null): boolean {
  if (!batch?.meta) return false;
  if (batch.meta.ipttqBankShortfall === true) return true;
  const ipttq = batch.meta.ipttq;
  if (!ipttq || typeof ipttq !== 'object' || Array.isArray(ipttq)) return false;
  const records = (ipttq as { records?: unknown }).records;
  if (!Array.isArray(records)) return false;
  return records.some((row) => {
    if (!row || typeof row !== 'object') return false;
    const fm = (row as { factoryMeta?: { bankOk?: unknown } }).factoryMeta;
    return fm && fm.bankOk === false;
  });
}

/** Batch pipeline ready + Owner release gate pending. */
function isGoodsReadyAwaitingOwner(batch?: FactoryBatch | null): boolean {
  if (!batch) return false;
  return batch.releaseAuthorization === 'awaiting_owner_auth';
}

function isGoodsPipelineReady(batch?: FactoryBatch | null): boolean {
  if (!batch) return false;
  return batch.status === 'ready' || batch.warehouseStatus === 'ready';
}

function extractFailedOf(batch?: FactoryBatch | null): number {
  if (!batch) return 0;
  const n = Number(batch.meta?.extractFailed ?? 0);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function isGoodsReleasing(batch?: FactoryBatch | null): boolean {
  return batch?.releaseAuthorization === 'authorized';
}

function asBankDrafts(raw: unknown): BankDraft[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((d) => d && typeof d === 'object') as BankDraft[];
}

function memberIsRecycle(m: FactoryTtqProductMember): boolean {
  if (m.membershipRole === 'recycle') return true;
  return String(m.snapshot?.membershipRole || '').trim().toLowerCase() === 'recycle';
}

function memberIsReuse(m: FactoryTtqProductMember): boolean {
  return m.snapshot?.reuse === true || (m.snapshot?.factoryProvenance as { reuse?: unknown } | undefined)?.reuse === true;
}

/** Stem preview from member snapshot only (no batch-wide structural units fetch). */
function memberStemPreview(m: FactoryTtqProductMember): string {
  const snap = (m.snapshot || {}) as Record<string, unknown>;
  const subQuestions = Array.isArray(snap.subQuestions)
    ? snap.subQuestions.map((s) => String(s || '').trim()).filter(Boolean)
    : [];
  const subAnswers = Array.isArray(snap.subAnswers)
    ? snap.subAnswers.map((s) => String(s || '').trim()).filter(Boolean)
    : [];
  const qa = resolveAdmissionParticleQa({
    particleQuestions: String(snap.particleQuestions || snap.particleTitle || ''),
    particleAnswers: String(snap.particleAnswers || snap.answer || ''),
    particleTitle: String(snap.particleTitle || ''),
    subQuestions,
    subAnswers,
    extractQuestion: undefined,
    extractAnswer: undefined,
    extractSubQuestions: undefined,
    extractSubAnswers: undefined,
    pap: (snap.pap || null) as Record<string, unknown> | null,
  });
  const parts = [qa.question, ...qa.subQuestions.slice(0, 2)].filter(Boolean);
  return truncateMathAwarePreview(parts.join(' ') || m.particleId, 120);
}

function productStatsFromAdmission(admission: FactoryTtqAdmissionResult | null) {
  const rows = admission?.warehouseTtqas || [];
  let created = 0;
  let joined = 0;
  let critical = 0;
  const productIds = new Set<string>();
  for (const row of rows) {
    const d = row.factoryMeta?.disposition;
    if (d === 'created') created += 1;
    else if (d === 'joined') joined += 1;
    else if (d === 'critical') critical += 1;
    const pid = String(row.factoryMeta?.productId || '').trim();
    if (pid) productIds.add(pid);
  }
  return {
    created,
    joined,
    critical,
    productCount: productIds.size,
    groupCount: rows.length,
  };
}

const GOODS_SCOPE_KEY = 'factory.goodsListScope';
type GoodsListScope = 'order' | 'all';

function readGoodsListScope(): GoodsListScope {
  try {
    return sessionStorage.getItem(GOODS_SCOPE_KEY) === 'all' ? 'all' : 'order';
  } catch {
    return 'order';
  }
}

function writeGoodsListScope(scope: GoodsListScope) {
  try {
    sessionStorage.setItem(GOODS_SCOPE_KEY, scope);
  } catch {
    /* ignore */
  }
}

function productTouchesOrder(product: FactoryTtqProduct, scopedBatchIds: Set<string>): boolean {
  const source = String(product.sourceBatchId || '').trim();
  if (source && scopedBatchIds.has(source)) return true;
  for (const id of product.memberBatchIds || []) {
    if (scopedBatchIds.has(String(id || '').trim())) return true;
  }
  return false;
}

/**
 * 顶层「成品智能体」：扁平列表 / 详情（含诱导题）/ 按任务贡献。
 * 成品智能体是后续释放知识颗粒到复习的来源。
 */
export function FactoryGoodsPage() {
  const { refreshKey = 0 } = useOutletContext<FactoryDeskOutletContext>() ?? { refreshKey: 0 };
  const { activeOrderId } = useFactoryOrderScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchId = String(searchParams.get('batchId') || '').trim();
  const productId = String(searchParams.get('productId') || '').trim();

  const [products, setProducts] = useState<FactoryTtqProduct[]>([]);
  const [critical, setCritical] = useState<FactoryCriticalZoneItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [goodsScope, setGoodsScope] = useState<GoodsListScope>(readGoodsListScope);

  const [batch, setBatch] = useState<FactoryBatch | null>(null);
  const [admission, setAdmission] = useState<FactoryTtqAdmissionResult | null>(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [resolveBusy, setResolveBusy] = useState(false);
  const [jobProgress, setJobProgress] = useState('');

  const [detailProduct, setDetailProduct] = useState<FactoryTtqProduct | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankMembers, setBankMembers] = useState<FactoryTtqProductMember[]>([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);
  const [sendOwnerBusyId, setSendOwnerBusyId] = useState<string | null>(null);

  const showToast = useCallback((message: string, durationMs = 2400) => {
    setToast(message);
    window.setTimeout(() => setToast(null), durationMs);
  }, []);

  const reloadGlobal = useCallback(async () => {
    const [p, c] = await Promise.all([
      factoryService.listTtqProducts(),
      factoryService.listTtqCriticalZone({ status: 'pending' }),
    ]);
    const batchIds = [...new Set([
      ...c.map((it) => String(it.batchId || '').trim()).filter(Boolean),
      ...p.map((it) => String(it.sourceBatchId || '').trim()).filter(Boolean),
    ])];
    let toastBits: string[] = [];
    if (batchIds.length) {
      for (const bid of batchIds) {
        const cleaned = await factoryService.cleanupStaleTtqCritical(bid).catch(() => null);
        if (cleaned?.rejected || cleaned?.archived) {
          toastBits.push(cleaned.message || '');
        }
      }
      const [p2, c2] = await Promise.all([
        factoryService.listTtqProducts(),
        factoryService.listTtqCriticalZone({ status: 'pending' }),
      ]);
      setProducts(p2);
      setCritical(c2);
      if (toastBits.length) {
        showToast(toastBits.filter(Boolean).join('；') || '已清理残留', 3200);
      }
      return;
    }
    setProducts(p);
    setCritical(c);
  }, [showToast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        if (refreshKey > 0) setLoading(true);
        await reloadGlobal();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadGlobal, refreshKey]);

  useEffect(() => {
    if (!batchId || productId) {
      if (!batchId) {
        setBatch(null);
        setAdmission(null);
        setJobProgress('');
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setTaskLoading(true);
        setError(null);
        const [detail, admRes] = await Promise.all([
          factoryService.getBatch(batchId),
          factoryService.getTtqAdmission(batchId).catch(() => null),
        ]);
        if (cancelled) return;
        setBatch(detail.batch);
        setAdmission(admRes?.ttqAdmission ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setBatch(null);
          setAdmission(null);
        }
      } finally {
        if (!cancelled) setTaskLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId, productId, refreshKey]);

  useEffect(() => {
    if (!productId) {
      setDetailProduct(null);
      setBankOpen(false);
      setBankMembers([]);
      setBankError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setDetailLoading(true);
        setError(null);
        // First paint: product meta + bank drafts only (no members / no batch units).
        const res = await factoryService.getTtqProduct(productId, { includeMembers: false });
        if (cancelled) return;
        setDetailProduct(res.product);
        setBankMembers([]);
        setBankError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setDetailProduct(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId, refreshKey]);

  const openOriginalBank = useCallback(async () => {
    if (!productId) return;
    setBankOpen(true);
    if (bankMembers.length > 0) return;
    try {
      setBankLoading(true);
      setBankError(null);
      const res = await factoryService.listTtqProductMembers(productId);
      setBankMembers(res.members || []);
    } catch (err) {
      setBankError(err instanceof Error ? err.message : String(err));
    } finally {
      setBankLoading(false);
    }
  }, [productId, bankMembers.length]);

  const productStats = useMemo(() => productStatsFromAdmission(admission), [admission]);
  const needsResolve = useMemo(
    () => (admission?.warehouseTtqas || []).some((r) => {
      const d = r.factoryMeta?.disposition;
      // critical = 旧「切 overflow 入临界」脏数据，允许重跑整组新建
      return !d || d === 'critical';
    }),
    [admission],
  );

  const onResolve = useCallback(async () => {
    if (!batchId || resolveBusy) return;
    try {
      setResolveBusy(true);
      setJobProgress('写入成品智能体仓…');
      const res = await factoryService.resolveTtqProducts(batchId);
      if (res.ttqAdmission) setAdmission(res.ttqAdmission);
      await reloadGlobal();
      showToast('已写入成品智能体仓');
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), 4000);
    } finally {
      setResolveBusy(false);
      setJobProgress('');
    }
  }, [batchId, resolveBusy, reloadGlobal, showToast]);

  useEffect(() => {
    if (productId || !batchId || !needsResolve || resolveBusy || taskLoading || !admission) return;
    void onResolve();
  }, [admission, batchId, needsResolve, onResolve, productId, resolveBusy, taskLoading]);

  const showList = !batchId && !productId;
  const showTask = Boolean(batchId) && !productId;
  const showDetail = Boolean(productId);

  const pendingByBatch = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of critical) {
      const bid = String(item.batchId || '').trim();
      if (!bid) continue;
      map.set(bid, (map.get(bid) || 0) + 1);
    }
    return map;
  }, [critical]);

  const [batchTitles, setBatchTitles] = useState<Map<string, string>>(() => new Map());
  const [batchById, setBatchById] = useState<Map<string, FactoryBatch>>(() => new Map());
  useEffect(() => {
    if (!showList) return;
    let cancelled = false;
    (async () => {
      try {
        const batches = await factoryService.listBatches();
        if (cancelled) return;
        const titles = new Map<string, string>();
        const byId = new Map<string, FactoryBatch>();
        for (const b of batches) {
          titles.set(b.id, String(b.title || '').trim() || `任务 ${b.id.slice(0, 8)}`);
          byId.set(b.id, b);
        }
        setBatchTitles(titles);
        setBatchById(byId);
      } catch {
        /* soft */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showList, refreshKey]);

  const scopedBatchIds = useMemo(() => {
    const ids = new Set<string>();
    if (!activeOrderId) return ids;
    for (const [id, b] of batchById) {
      if (b.orderId === activeOrderId) ids.add(id);
    }
    return ids;
  }, [activeOrderId, batchById]);

  const filterByOrder = Boolean(activeOrderId) && goodsScope === 'order';

  const productGroups = useMemo(() => {
    const visible = filterByOrder
      ? products.filter((p) => productTouchesOrder(p, scopedBatchIds))
      : products;
    const byBatch = new Map<string, FactoryTtqProduct[]>();
    const orphan: FactoryTtqProduct[] = [];
    for (const p of visible) {
      const bid = String(p.sourceBatchId || '').trim();
      if (!bid) {
        orphan.push(p);
        continue;
      }
      const list = byBatch.get(bid) || [];
      list.push(p);
      byBatch.set(bid, list);
    }
    const groups = [...byBatch.entries()].map(([bid, items]) => ({
      batchId: bid,
      batchTitle: `${
        filterByOrder && !scopedBatchIds.has(bid) ? '并入 · ' : ''
      }${batchTitles.get(bid) || `任务 ${bid.slice(0, 8)}…`}`,
      items: [...items].sort(
        (a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
      ),
    }));
    groups.sort((a, b) => {
      const at = a.items[0]?.createdAt || '';
      const bt = b.items[0]?.createdAt || '';
      return String(bt).localeCompare(String(at));
    });
    if (orphan.length && !filterByOrder) {
      groups.push({
        batchId: '',
        batchTitle: '无来源任务',
        items: orphan,
      });
    }
    return groups;
  }, [filterByOrder, products, batchTitles, scopedBatchIds]);

  const openHome = () => setSearchParams({});
  const openBatch = (id: string) => setSearchParams(id ? { batchId: id } : {});
  const openProduct = (id: string) => setSearchParams({ productId: id });

  const refreshBatchMap = useCallback(async () => {
    const batches = await factoryService.listBatches();
    const titles = new Map<string, string>();
    const byId = new Map<string, FactoryBatch>();
    for (const b of batches) {
      titles.set(b.id, String(b.title || '').trim() || `任务 ${b.id.slice(0, 8)}`);
      byId.set(b.id, b);
    }
    setBatchTitles(titles);
    setBatchById(byId);
    return byId;
  }, []);

  const onSendOwnerAccept = useCallback(
    async (batchId: string) => {
      if (!batchId || sendOwnerBusyId) return;
      try {
        setSendOwnerBusyId(batchId);
        const res = await factoryService.requestOwnerAccept(batchId);
        await refreshBatchMap();
        showToast(res.message || '已发送 Owner 等待授权验收');
      } catch (err) {
        showToast(err instanceof Error ? err.message : String(err), 4200);
      } finally {
        setSendOwnerBusyId(null);
      }
    },
    [sendOwnerBusyId, refreshBatchMap, showToast],
  );

  const bankDrafts = asBankDrafts(detailProduct?.bankDrafts);

  return (
    <div className="space-y-4">
      <header>
        {showList ? null : (
          <button
            type="button"
            className="mb-1 text-xs text-primary hover:underline"
            onClick={openHome}
          >
            ← 返回列表
          </button>
        )}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-ink">成品智能体</h1>
            <p className="mt-1 text-sm text-muted">
              {showDetail
                ? '成品智能体详情 · 含将释放到复习的诱导题库'
                : showTask
                  ? '本任务对成品智能体仓的贡献'
                  : filterByOrder
                    ? '本订单贡献的成品智能体 · 点条目进详情。含本单新建与并入已有仓的条目。'
                    : '全部成品智能体仓 · 点条目进详情。这些智能体是后续释放知识颗粒到复习的来源。'}
            </p>
          </div>
          {showList ? (
            <button
              type="button"
              role="switch"
              aria-checked={!filterByOrder}
              disabled={!activeOrderId}
              title={
                activeOrderId
                  ? filterByOrder
                    ? '当前按本订单贡献过滤，点击显示全部'
                    : '当前显示全部，点击按本订单贡献过滤'
                  : '请先选择当前订单'
              }
              onClick={() => {
                const next: GoodsListScope = goodsScope === 'order' ? 'all' : 'order';
                setGoodsScope(next);
                writeGoodsListScope(next);
              }}
              className="shrink-0 rounded-md border border-[#3a4450] px-3 py-1.5 text-xs font-medium text-[#2EC4B6] hover:border-[#2EC4B6]/50 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {filterByOrder ? '已按照订单过滤' : '已显示全部'}
            </button>
          ) : null}
        </div>
      </header>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {toast ? <p className="text-sm text-ink">{toast}</p> : null}

      {showDetail ? (
        detailLoading ? (
          <p className="text-sm text-muted">加载成品智能体…</p>
        ) : detailProduct ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-ink">
                  {detailProduct.title || '未命名成品智能体'}
                </h2>
                <p className="mt-1 text-xs text-muted">
                  {detailProduct.subjectCode === 'yuwen' ? '语文' : '数学'}
                  {' · 成员 '}
                  {detailProduct.memberCount}/{detailProduct.memberCap}
                  {Number(detailProduct.recycleCount) > 0
                    ? ` · 原题 ${detailProduct.recycleCount}`
                    : ''}
                  {' · 诱导 '}
                  {bankDrafts.length}
                  {detailProduct.sourceBatchId ? (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => openBatch(detailProduct.sourceBatchId!)}
                      >
                        来源任务贡献
                      </button>
                    </>
                  ) : null}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void openOriginalBank()}
                className="shrink-0 rounded-lg border border-border bg-[#222a34] px-3 py-1.5 text-xs font-medium text-ink hover:border-primary/50 hover:text-primary"
              >
                查看原始题库
              </button>
            </div>

            {detailProduct.thinkingLineage ? (
              <section className="rounded-xl border border-border bg-[#1a2028] p-4">
                <h3 className="text-base font-semibold tracking-wide text-ink">思考脉络</h3>
                <div className="mt-2 text-sm text-ink">
                  <MathTextBlock text={detailProduct.thinkingLineage} className="text-sm" />
                </div>
              </section>
            ) : null}

            <section className="rounded-xl border border-border bg-[#1a2028] p-4">
              <h3 className="text-base font-semibold tracking-wide text-ink">
                诱导题库（{bankDrafts.length}）
              </h3>
              {bankDrafts.length === 0 ? (
                <p className="mt-2 text-sm text-muted">尚无诱导题草稿</p>
              ) : (
                <ul className="mt-2 divide-y divide-border/60">
                  {bankDrafts.map((d, i) => (
                    <li key={d.id || `bank-${i}`} className="py-3">
                      <div className="text-sm font-medium text-ink">
                        <span className="mr-1 tabular-nums text-muted">({i + 1})</span>
                        <MathTextBlock
                          inline
                          text={d.title || d.metadata?.displayTitle || '诱导题'}
                          className="text-sm font-medium"
                        />
                      </div>
                      {d.questions?.main ? (
                        <div className="mt-2 text-sm text-ink">
                          <MathTextBlock text={d.questions.main} className="text-sm" />
                        </div>
                      ) : null}
                      {(() => {
                        const figureUrl = String(d.metadata?.relatedGraphEmbeddedImage?.sourceImageUrl || '').trim();
                        const figureStatus = String(d.metadata?.inducedFigure?.status || '').trim();
                        const kind = String(
                          d.metadata?.inducedFigure?.embedImageKind
                          || d.metadata?.embedImageKind
                          || '',
                        ).trim().toUpperCase();
                        if (!figureUrl && !figureStatus && !d.metadata?.needsEmbeddedImage) return null;
                        return (
                          <div className="mt-2 flex items-start gap-3">
                            {figureUrl ? (
                              <img
                                src={figureUrl}
                                alt="本题题图"
                                className="h-24 w-24 shrink-0 rounded-md object-contain bg-white"
                              />
                            ) : null}
                            <p className="text-xs text-muted">
                              {kind === 'GT' ? '几何题图 · DQA' : kind === 'NGT' ? '本题题图 · NGT' : '本题题图'}
                              {figureStatus && figureStatus !== 'ready' ? ` · ${figureStatus}` : ''}
                              {d.metadata?.inducedFigure?.errorMessage
                                ? ` · ${d.metadata.inducedFigure.errorMessage}`
                                : ''}
                            </p>
                          </div>
                        );
                      })()}
                      {d.answers?.main ? (
                        <div className="mt-2 text-sm text-muted">
                          答：
                          <MathTextBlock text={d.answers.main} className="text-sm" />
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : (
          <p className="text-sm text-muted">未找到该成品智能体</p>
        )
      ) : null}

      {bankOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onClick={() => setBankOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="原始题库"
            className="flex max-h-[min(80vh,640px)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-[#1a2028] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
              <div className="min-w-0">
                <h3 className="truncate text-base font-semibold text-ink">原始题库</h3>
                <p className="mt-0.5 text-xs text-muted">
                  {detailProduct?.title || '成品智能体成员颗粒'}
                  {bankMembers.length > 0
                    ? ` · 入围 ${bankMembers.filter((m) => !memberIsRecycle(m)).length} · 原题 ${bankMembers.filter(memberIsRecycle).length}`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 text-xs text-muted hover:text-ink"
                onClick={() => setBankOpen(false)}
              >
                关闭
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {bankLoading ? (
                <p className="text-sm text-muted">加载原始题库…</p>
              ) : bankError ? (
                <p className="text-sm text-danger">{bankError}</p>
              ) : bankMembers.length === 0 ? (
                <p className="text-sm text-muted">暂无成员颗粒</p>
              ) : (
                <div className="space-y-4">
                  <ul className="divide-y divide-border/40">
                    {bankMembers.filter((m) => !memberIsRecycle(m)).map((m, i) => {
                      const stem = memberStemPreview(m);
                      return (
                        <li key={m.id} className="py-2.5 text-sm text-ink">
                          <span className="mr-1.5 tabular-nums text-muted">({i + 1})</span>
                          {memberIsReuse(m) ? (
                            <span className="mr-1.5 text-[11px] text-muted">复用</span>
                          ) : null}
                          <MathTextBlock text={stem || m.particleId} className="text-sm" />
                        </li>
                      );
                    })}
                  </ul>
                  {bankMembers.some(memberIsRecycle) ? (
                    <div>
                      <h4 className="text-xs font-medium tracking-wide text-muted">
                        原题释放源（recycle，不参与诱导）
                      </h4>
                      <ul className="mt-1 divide-y divide-border/40">
                        {bankMembers.filter(memberIsRecycle).map((m, i) => {
                          const stem = memberStemPreview(m);
                          const reason = String(m.snapshot?.recycleReason || '').trim();
                          return (
                            <li key={m.id} className="py-2.5 text-sm text-ink">
                              <span className="mr-1.5 tabular-nums text-muted">({i + 1})</span>
                              <MathTextBlock text={stem || m.particleId} className="text-sm" />
                              {reason ? (
                                <span className="mt-0.5 block text-[11px] text-muted">{reason}</span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showTask ? (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-ink">
              <span className="text-muted">任务贡献 · </span>
              {batch?.title || batchId.slice(0, 8)}
              {' · '}
              <Link
                to={`/factory/tasks/${batchId}/results?phase=ttq`}
                className="text-primary hover:underline"
              >
                打开任务结果
              </Link>
            </div>
            <button
              type="button"
              disabled={resolveBusy || !admission}
              onClick={() => void onResolve()}
              className="text-xs text-primary hover:underline disabled:opacity-50"
            >
              {resolveBusy ? '写入中…' : '重新写入成品智能体仓'}
            </button>
          </div>
          {taskLoading ? (
            <p className="text-sm text-muted">加载任务入围…</p>
          ) : (
            <TaskProductsOpsPanel
              batchId={batchId}
              batch={batch}
              admission={admission}
              productStats={productStats}
              resolveBusy={resolveBusy}
              needsResolve={needsResolve}
              jobProgress={jobProgress}
              onAdmissionChange={setAdmission}
              onToast={showToast}
            />
          )}
        </div>
      ) : null}

      {showList && [...pendingByBatch.entries()].some(([bid]) => !filterByOrder || scopedBatchIds.has(bid)) ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5">
          <div className="text-sm font-medium text-amber-100">
            待人审 · {[...pendingByBatch.entries()]
              .filter(([bid]) => !filterByOrder || scopedBatchIds.has(bid))
              .reduce((n, [, c]) => n + c, 0)} 条
          </div>
          <ul className="mt-2 space-y-1">
            {[...pendingByBatch.entries()]
              .filter(([bid]) => !filterByOrder || scopedBatchIds.has(bid))
              .map(([bid, n]) => (
              <li key={bid} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate text-muted">
                  {batchTitles.get(bid) || `任务 ${bid.slice(0, 8)}…`}
                  <span className="tabular-nums"> · {n} 条</span>
                </span>
                <button
                  type="button"
                  className="shrink-0 text-primary hover:underline"
                  onClick={() => openBatch(bid)}
                >
                  打开贡献
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showList ? (
        <div className="space-y-5">
          {loading ? <p className="text-sm text-muted">加载中…</p> : null}
          {!loading && products.length === 0 ? (
            <p className="text-sm text-muted">暂无成品智能体。完成品题推荐后会自动出现。</p>
          ) : null}
          {!loading && products.length > 0 && productGroups.length === 0 ? (
            <p className="text-sm text-muted">
              {filterByOrder
                ? '当前订单暂无贡献的成品智能体。可切换到「已显示全部」查看仓内其它条目。'
                : '暂无成品智能体。'}
            </p>
          ) : null}
          {productGroups.map((group) => {
            const nearDup = group.items.length > 1
              && group.items.every((p) => p.memberCount === group.items[0]?.memberCount);
            const sourceBatch = group.batchId ? batchById.get(group.batchId) : undefined;
            const failN = extractFailedOf(sourceBatch);
            const releasing = isGoodsReleasing(sourceBatch);
            const awaitingOwner = isGoodsReadyAwaitingOwner(sourceBatch);
            const pipelineReady = isGoodsPipelineReady(sourceBatch);
            const bankShort = group.items.some(productBankShort)
              || batchMetaBankShortfall(sourceBatch);
            const canSendOwner =
              Boolean(group.batchId)
              && pipelineReady
              && !releasing
              && !awaitingOwner
              && !bankShort
              && Boolean(sourceBatch?.orderId);
            const showTray =
              Boolean(group.batchId)
              && (failN > 0 || canSendOwner || awaitingOwner);
            return (
              <section key={group.batchId || 'orphan'} className="space-y-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-2">
                  <h2 className="text-xs font-medium tracking-wide text-muted">
                    {group.batchTitle}
                    <span className="ml-2 tabular-nums font-normal">
                      {group.items.length} 个成品智能体
                    </span>
                  </h2>
                  {nearDup ? (
                    <span className="text-[11px] text-amber-200/90">
                      同任务多条近重复（历史重跑残留）
                    </span>
                  ) : null}
                  {group.batchId ? (
                    <button
                      type="button"
                      className="text-[11px] text-primary hover:underline"
                      onClick={() => openBatch(group.batchId)}
                    >
                      贡献视图
                    </button>
                  ) : null}
                </div>
                {showTray ? (
                  <div
                    className={`mb-1 flex flex-wrap items-center gap-2 rounded-lg border px-2.5 py-2 ${
                      failN > 0
                        ? 'border-amber-500/40 bg-amber-500/10'
                        : awaitingOwner
                          ? 'border-emerald-500/30 bg-emerald-500/10'
                          : 'border-border/80 bg-[#1a2028]'
                    }`}
                  >
                    {failN > 0 ? (
                      <>
                        <TriangleAlert
                          size={14}
                          strokeWidth={2.25}
                          className="shrink-0 text-amber-300"
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1 text-[11px] leading-snug text-amber-100/95">
                          来源任务有 {failN} 页知识颗粒提取失败；修复前勿发送 Owner 验收。
                        </span>
                      </>
                    ) : awaitingOwner ? (
                      <span className="min-w-0 flex-1 text-[11px] text-emerald-200/90">
                        已发送 Owner，等待授权验收。
                      </span>
                    ) : (
                      <span className="min-w-0 flex-1 text-[11px] text-muted">
                        产线已就绪；确认无失败页后，手动发送 Owner 验收。
                      </span>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {failN > 0 && group.batchId ? (
                        <Link
                          to={`/factory/tasks/${group.batchId}/results?phase=extract&warn=extract`}
                          className="rounded-md border border-amber-500/45 bg-amber-500/15 px-2 py-1 text-[11px] font-semibold text-amber-200 hover:bg-amber-500/25"
                        >
                          去修复
                        </Link>
                      ) : null}
                      {canSendOwner ? (
                        <button
                          type="button"
                          disabled={failN > 0 || sendOwnerBusyId === group.batchId}
                          title={
                            failN > 0
                              ? '请先修复提取失败页'
                              : '发送给 Owner 等待授权验收'
                          }
                          onClick={() => void onSendOwnerAccept(group.batchId)}
                          className="rounded-md border border-primary/40 bg-primary/15 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/25 disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          {sendOwnerBusyId === group.batchId
                            ? '发送中…'
                            : '发送 Owner 验收'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {group.items.map((p) => {
                    const bankN = Array.isArray(p.bankDrafts) ? p.bankDrafts.length : 0;
                    const bankTarget = resolveIpttqBankTarget(Number(p.memberCount) || 0, p.meta);
                    const bankOk = bankN >= bankTarget;
                    const created = p.createdAt
                      ? new Date(p.createdAt).toLocaleString('zh-CN', {
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                      : '';
                    const itemBatch = batchById.get(String(p.sourceBatchId || group.batchId || ''));
                    const itemReleasing = isGoodsReleasing(itemBatch);
                    const itemAwaiting = !itemReleasing && isGoodsReadyAwaitingOwner(itemBatch);
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          className="flex h-full w-full items-start gap-3 rounded-xl border border-border bg-[#1a2028] p-3 text-left transition hover:border-primary/45 hover:bg-[#1d242e]"
                          onClick={() => openProduct(p.id)}
                        >
                          <span
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary"
                            aria-hidden
                          >
                            <Bot size={18} strokeWidth={2.1} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[11px] text-muted">成品智能体</span>
                            <span className="mt-0.5 block font-medium leading-snug text-ink">
                              {p.title || '未命名成品智能体'}
                            </span>
                            <span className="mt-1 block text-xs text-muted">
                              {p.subjectCode === 'yuwen' ? '语文' : '数学'}
                              {' · 成员 '}
                              {p.memberCount}/{p.memberCap}
                              {Number(p.recycleCount) > 0 ? ` · 原题 ${p.recycleCount}` : ''}
                              {' · 诱导 '}
                              <span className={bankOk ? undefined : 'text-amber-200/95'}>
                                {bankN}/{bankTarget}
                              </span>
                              {created ? ` · ${created}` : ''}
                            </span>
                            {itemReleasing ? (
                              <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-success">
                                <Droplets size={12} strokeWidth={2.2} className="factory-droplets-drip" aria-hidden />
                                颗粒释放中
                              </span>
                            ) : itemAwaiting ? (
                              <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-300/90">
                                <Check size={12} strokeWidth={2.4} aria-hidden />
                                已发送Owner等待授权
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
